import { inject, Injectable } from '@angular/core';
import { TrackingService } from '@ds24/tracking-ng';
import { EventPayloadMap, TrackingEventType } from '@ds24/tracking-core/types/events';
import { BackendProxyProvider } from '@ds24/tracking-core/providers/backend-proxy-provider';
import { ApmProvider } from '@ds24/tracking-core/providers/apm-provider';
import { OrderFormPayloadBaseType } from '@ds24/tracking-core/types/events/OrderForm';
import { combineLatest, firstValueFrom, map, Subscription } from 'rxjs';
import { OrderformFascade } from '../fascades/orderform.fascade';
import { performanceForMetric } from '../misc/performance';
import { getBrowserInfo, getDeviceType } from '../misc/browser-info';
import { GrowthBookService } from './growthbook.service';
import { isPageBuilderDesignMode, isPreviewMode } from '../misc/pagebuilder';

@Injectable({
  providedIn: 'root',
})
export class OrderformTrackingCollectorService {
  readonly #trackEvents: ReadonlySet<string> = new Set([
    'OrderFormLoaded',
    'OrderFormStart',
    'BuyButtonClick',
    'ExperimentViewed',
    'BuyCompleted',
  ]);

  #tracking = inject(TrackingService);
  readonly #growthbookService = inject(GrowthBookService);
  #orderFormFascade = inject(OrderformFascade);

  #orderMetaDataSubscription: Subscription;

  #orderFormPayload: OrderFormPayloadBaseType;
  #growthbookExperimentSubscription: Subscription;

  public async initialize() {
    this.#tracking.registerProvider(new BackendProxyProvider());
    const sessionId = await this.#orderFormFascade.anonymousSessionId;
    this.#tracking.addMetadata({
      sessionId,
    });

    this.#orderFormPayload = {
      iframeEmbedded: window.parent !== window,
      mainProductId: await firstValueFrom(this.#orderFormFascade.mainProductId$),
      mainProductTypeId: await firstValueFrom(this.#orderFormFascade.mainProductTypeId$),
      defaultPrice: await firstValueFrom(this.#orderFormFascade.mainProductDefaultPrice$),
      siteownerId: await firstValueFrom(this.#orderFormFascade.siteownerId$),
      pageType:
        window.location.pathname.startsWith('/product') ||
        window.location.pathname.startsWith('/checkout') ||
        window.location.pathname.startsWith('/offer')
          ? 'orderform'
          : 'renew',
      orderType: window.location.search.includes('upsell_session_id') ? 'upsell' : 'initial',
      vendor: await firstValueFrom(this.#orderFormFascade.merchantName$),
      affiliate: await firstValueFrom(this.#orderFormFascade.affiliateName$),
      orderformId: await firstValueFrom(this.#orderFormFascade.config$.pipe(map((config) => config.orderform_id))),
      cookieConsentVisible: await firstValueFrom(this.#orderFormFascade.hasCookieConsent$),
    };

    const userProperties = {};

    this.#growthbookExperimentSubscription = this.#growthbookService
      .watchExperimentComplete()
      .subscribe(({ experiment, result }) => {
        this.#sendEvent('ExperimentViewed', {
          experimentKey: experiment.key,
          experimentVariation: '' + result.variationId,
        });
      });

    /**
     * @todo: remove after pubsub migration is done
     */
    const experiments = await this.#orderFormFascade.viewedExperiments;
    experiments.forEach((experiment) => {
      userProperties[`experiment_${experiment.key}_enabled`] = true;
      userProperties[`experiment_${experiment.key}_variation`] = '' + experiment.variation;
    });
    this.#tracking.addMetadata({
      userProperties,
    });

    const isGdprApplied = await firstValueFrom(this.#orderFormFascade.isGdprApplied$);
    if (!isGdprApplied) {
      this.#orderFormPayload.referrer = document.referrer || undefined;
      const userAgent = navigator.userAgent;
      const { browser, version, app, os } = getBrowserInfo(userAgent);

      this.#orderFormPayload.os = os;
      this.#orderFormPayload.browser = browser;
      this.#orderFormPayload.browserVersion = version;
      this.#orderFormPayload.app = app;
      this.#orderFormPayload.deviceType = getDeviceType(userAgent);
      this.#orderFormPayload.screenWidth = window.screen.width;
      this.#orderFormPayload.screenHeight = window.screen.height;
    }

    this.#orderMetaDataSubscription = combineLatest([
      this.#orderFormFascade.order$,
      this.#orderFormFascade.isExpressCheckoutClicked$,
    ]).subscribe(([order, isExpressCheckoutClicked]) => {
      this.#orderFormPayload = {
        ...this.#orderFormPayload,
        orderCheckoutType: isExpressCheckoutClicked ? 'express' : 'default',
        orderBuyerCountry: order.settings.config.buyer_country,
        orderLanguage: order.settings.global.language,
        orderCurrency: order.order.currency,
        orderValue: order.order_summary.order_final.gross_1st,
        paymentMethod: isExpressCheckoutClicked
          ? order.express_checkout?.pay_method
          : order.order.payment_provider.renderer_type,
        paymentProviderId: String(
          isExpressCheckoutClicked
            ? order.express_checkout?.payment_provider_id
            : order.order.payment_provider.payment_provider_id
        ),
        orderBillingType: order.order_summary.payment_plan.billing_type,
      };
    });

    if (isPageBuilderDesignMode() || isPreviewMode()) {
      return;
    }

    const initialError = await firstValueFrom(this.#orderFormFascade.initialError$);

    this.#sendEvent('OrderFormLoaded', {
      ...this.#orderFormPayload,
      hasInitialError: initialError !== null,
      initialError,
    });

    this.trackOrderFormStart();
  }

  private async trackOrderFormStart() {
    let firstInput = await performanceForMetric('first-input');
    const isGdprApplied = await firstValueFrom(this.#orderFormFascade.isGdprApplied$);
    const firstContentfulPaint = isGdprApplied ? undefined : await performanceForMetric('paint');
    const largestContentfulPaint = isGdprApplied ? undefined : await performanceForMetric('largest-contentful-paint');
    if (firstInput === undefined) {
      firstInput = await performanceForMetric('first-input');
    }
    this.#sendEvent('OrderFormStart', {
      ...this.#orderFormPayload,
      largestContentfulPaint,
      firstContentfulPaint,
      firstInput,
    });
  }

  public track<T extends TrackingEventType>(
    event: T,
    payload?: Partial<Extract<EventPayloadMap[T], OrderFormPayloadBaseType>>
  ) {
    if (isPageBuilderDesignMode() || isPreviewMode()) {
      return;
    }

    if (!this.#orderFormPayload) {
      return;
    }

    this.#sendEvent(event, {
      ...this.#orderFormPayload,
      ...payload,
    } as any);
  }

  #sendEvent<T extends TrackingEventType>(event: T, payload?: EventPayloadMap[T]) {
    if (this.#trackEvents.has(String(event))) {
      this.#tracking.critical(event, payload);
      return;
    }

    this.#tracking.debug(event, payload);
  }

  public destroy() {
    this.#tracking.unregisterProvider('backend-proxy');
    this.#orderMetaDataSubscription?.unsubscribe();
    this.#growthbookExperimentSubscription?.unsubscribe();
  }
}
